import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Delete } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'

type Props = {
  nodeId: string
  handleDelete?: () => void
  title: string
  icon: React.ReactNode
  backgroundColor: string
  showDeleteButton?: boolean
}

// Title bar and duplicate & delete icons
export const SidepanelHeader = ({
  nodeId,
  handleDelete: handleDeleteProp,
  title,
  icon,
  backgroundColor,
  showDeleteButton = true,
}: Props) => {
  const { openDialog } = useConfirmationDialog()
  const { setEdgesUI, setNodesUI } = useWorkflowContext()

  const handleDelete =
    handleDeleteProp ??
    (() => {
      openDialog({
        title: i18n.t('workflows.sidepanel.general.delete_confirmation.title'),
        description: i18n.t('workflows.sidepanel.general.delete_confirmation.description'),
        confirmLabel: i18n.t('common.delete'),
        onConfirm: () => {
          setEdgesUI((prevEdges) => {
            // Find parent edges (edges pointing TO the node being deleted)
            const parentEdges = prevEdges.filter((edge) => edge.target === nodeId)

            // Find child edges (edges FROM the node being deleted)
            const childEdges = prevEdges.filter((edge) => edge.source === nodeId)

            // Create new edges connecting parents directly to children
            const reconnectionEdges = parentEdges.flatMap((parentEdge) =>
              childEdges.map((childEdge) => ({
                ...parentEdge,
                id: `${parentEdge.source}-${childEdge.target}`,
                target: childEdge.target,
              }))
            )

            // Remove all edges connected to the deleted node and add reconnection edges
            return [
              ...prevEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
              ...reconnectionEdges,
            ]
          })

          // Remove the node from nodesUI
          setNodesUI((prevNodes) => prevNodes.filter((node) => nodeId !== node.id))
        },
      })
    })

  return (
    <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s12">
      <Box flexDirection="row" alignItems="center" gap="s12">
        {/* Placeholder icon */}
        <Box
          flexDirection="row"
          style={{ backgroundColor: backgroundColor }}
          borderRadius={{ all: 'abs012' }}
          width="s32"
          height="s32"
          alignItems="center"
          justifyContent="center"
        >
          {icon}
        </Box>
        <Text color="textPrimary" size="300" weight="medium">
          {title}
        </Text>
      </Box>

      {/* Duplicate & delete icons as placeholders */}
      {showDeleteButton && (
        <Box flexDirection="row" gap="s4" justifyContent="center" alignItems="center">
          <F0Button
            onClick={handleDelete}
            size="md"
            icon={Delete}
            hideLabel
            variant="critical"
            label={i18n.t('common.delete')}
          />
        </Box>
      )}
    </Box>
  )
}
